/**
 * Pure projection from a TanStack-shaped query result onto ViewState props.
 *
 * The decision table here is the loading→error→empty wiring that every list
 * view used to re-derive by hand. JSX/React-free so it runs under the
 * `node --test` strip-types sweep; the memoizing hook wrapper lives in
 * `use-view-state-integration.ts`.
 */
import type { ViewStateProps } from './view-state';
// Explicit `.ts` extension: this module is executed directly by the
// `node --experimental-strip-types --test` sweep, whose ESM resolver does not
// do extension inference.
import { resolveErrorMessage } from './error-message.ts';

/**
 * Structural subset of a `useApiQuery` / TanStack Query result. Structural on
 * purpose — no import from `@tyndo/hooks`, so any conforming result (including
 * `useApiQueryWithRetry` and hand-rolled fixtures in tests) plugs in.
 */
export interface ViewStateQuerySource<T> {
  data?: T;
  /** TanStack v5 spells first-load `isPending`; v4-style wrappers use `isLoading`. */
  isPending?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  error?: unknown;
  refetch?: () => unknown;
}

export interface ViewStateIntegrationOptions<T> {
  /**
   * When is loaded data "empty"? Defaults: arrays by `length === 0`;
   * `{ items: [...] }` list envelopes by `items.length === 0`; anything
   * else is never empty.
   */
  isEmpty?: (data: T) => boolean;
  /** Fallback copy when the error carries no usable message. */
  errorFallbackMessage?: string;
  /** Pass-through for the optimistic-write overlay. */
  optimistic?: boolean;
}

/**
 * Best-effort human message from an unknown error (ApiRequestError | Error |
 * ProblemDetails body | other). Delegates to the single ui-kit resolver so this
 * projection and `ErrorState` cannot drift — before P1-8.5 this one read only
 * `.message`, so an RFC-7807 body whose reason lives in `detail` fell through
 * to the fallback copy.
 */
export function deriveErrorMessage(error: unknown, fallback: string): string {
  return resolveErrorMessage(error, fallback);
}

function defaultIsEmpty(data: unknown): boolean {
  if (Array.isArray(data)) return data.length === 0;
  if (data && typeof data === 'object') {
    const items = (data as { items?: unknown }).items;
    if (Array.isArray(items)) return items.length === 0;
  }
  return false;
}

/** Project one query result into spread-ready ViewState props. */
export function deriveViewStateProps<T>(
  query: ViewStateQuerySource<T>,
  options?: ViewStateIntegrationOptions<T>,
): Partial<ViewStateProps> {
  const loading = Boolean(query.isPending ?? query.isLoading);
  const isError = Boolean(query.isError);
  const isEmpty = options?.isEmpty ?? defaultIsEmpty;
  const fallback = options?.errorFallbackMessage ?? 'The request failed. Try again.';

  return {
    loading,
    error: isError
      ? {
          message: deriveErrorMessage(query.error, fallback),
          retry: query.refetch ? () => void query.refetch!() : undefined,
        }
      : null,
    empty:
      !loading && !isError && query.data !== undefined && isEmpty(query.data as T),
    optimistic: options?.optimistic,
  };
}
